import React, { MouseEvent, useState, useEffect } from 'react'
import Select from 'react-select'

import {
  Breadcrumb,
  Container,
  Button,
  Card,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  Table,
} from 'react-bootstrap'
import useStripeSubscription from 'hooks/use-stripe-subscription'
import UserSearchAndSelectModal from 'components/user-search-and-select-modal'
import useAdminCreateOrganization from 'hooks/use-admin-create-organization'
import { useHistory } from 'react-router-dom'
import EditStripeSubscriptionIdModal from 'components/edit-stripe-subscription-id-modal'
import toast from 'react-hot-toast'
import { Organization } from 'interfaces/organization'
import { User } from 'interfaces/user'
import { useGetInternalAdmins } from 'hooks/use-get_internal-admins'

const OrganizationCreatePage = () => {
  const history = useHistory()
  const createOrganization = useAdminCreateOrganization()
  const { data: internalAdmins = [] } = useGetInternalAdmins()

  const [organization, setOrganization] = useState<Partial<Organization>>({
    name: '',
  })
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState<any>()
  const [searchStripeSubscription, setSearchStripeSubscription] =
    useState(false)
  const [newAdminUser, setNewAdminUser] = useState<User>()
  const [manualAmount, setManualAmount] = useState<string>('')
  const [manualSeatCount, setManualSeatCount] = useState<string>('')
  const [manualSaleRepId, setManualSaleRepId] = useState<string>('')
  const [
    showEditStripeSubscriptionIdModal,
    setShowEditStripeSubscriptionIdModal,
  ] = useState(false)
  const [showUserSearchAndSelectModal, setShowUserSearchAndSelectModal] =
    useState(false)

  const {
    data: stripeSubscription = {},
    isFetching: stripeSubscriptionIsFetching,
    isError: stripeSubscriptionIsError,
    error: stripeSubscriptionError,
  } = useStripeSubscription({
    stripeSubscriptionId,
    enabled: searchStripeSubscription,
  }) as any

  useEffect(() => {
    if (stripeSubscription && Object.keys(stripeSubscription).length > 0) {
      if (stripeSubscription.recurring_amount) {
        setManualAmount((stripeSubscription.recurring_amount / 100).toFixed(2))
      }

      if (stripeSubscription.seat_count) {
        setManualSeatCount(stripeSubscription.seat_count.toString())
      }

      if (stripeSubscription.sale_rep_id) {
        setManualSaleRepId(String(stripeSubscription.sale_rep_id))
      }
    }
  }, [stripeSubscription])

  const handleInputChange = (data: Partial<Organization>) => {
    setOrganization((_organization) => ({
      ..._organization,
      ...data,
    }))
  }

  const handleSearchStripeSubscriptionClick = () => {
    setShowEditStripeSubscriptionIdModal(true)
  }

  const handleUserButtonClick = (_user: User) => {
    setNewAdminUser(_user)
    setShowUserSearchAndSelectModal(false)
  }

  const handleSaveStripeSubscriptionIdClick = (
    newStripeSubscriptionId: string
  ) => {
    setStripeSubscriptionId(newStripeSubscriptionId)
    setShowEditStripeSubscriptionIdModal(false)
    setSearchStripeSubscription(true)
    setManualAmount('')
    setManualSeatCount('')
    setManualSaleRepId('')
  }

  const handleSaveClick = (e: MouseEvent) => {
    e.preventDefault()

    const amountInCents = Math.round(parseFloat(manualAmount || '0.00') * 100)
    const finalAmount = Math.max(0, amountInCents)

    const organizationData: Partial<Organization> = {
      name: organization.name,
      subscription_id: stripeSubscription.subscription_id,
      subscription_kind: 'stripe',
      subscription_status: stripeSubscription.subscription_status,
      product_identifier: stripeSubscription.product_identifier,
      currency: stripeSubscription.currency,
      billing_interval: stripeSubscription.billing_interval,
      stripe_customer_id: stripeSubscription.stripe_customer_id,
      trial_end: stripeSubscription.trial_end,
      deal_link: stripeSubscription.deal_link,
      user_id: newAdminUser?.id,
      seat_count: parseInt(manualSeatCount || '0'),
      subscription_amount: finalAmount.toString(),
      sale_rep_id: manualSaleRepId ? parseInt(manualSaleRepId) : null,
    }

    if (!organizationData.name) {
      toast('Organization name is required', { icon: '❌' })
      return
    }

    if (!organizationData.subscription_id) {
      toast('Stripe subscription is required', { icon: '❌' })
      return
    }

    createOrganization.mutate(
      { organizationData },
      {
        onSuccess: (data) => {
          history.push(`/organizations/${data.id}`)
        },
      }
    )
  }

  const amountFromSubscription = stripeSubscription.recurring_amount
    ? (stripeSubscription.recurring_amount / 100).toFixed(2)
    : null

  const seatCountFromSubscription = stripeSubscription.seat_count || 0
  const saleRepFromSubscription = stripeSubscription.sale_rep_id || ''

  const salesRepOptions = internalAdmins.map((admin) => ({
    value: String(admin.id),
    label: `${admin.name} (ID: ${admin.id})`,
  }))

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/organizations">Organizations</Breadcrumb.Item>
        <Breadcrumb.Item href="/organizations/new" active>
          New Organization
        </Breadcrumb.Item>
      </Breadcrumb>
      <div style={{ maxWidth: '1200px' }}>
        <h3 className="mt-4 mb-4">Create New Organization</h3>
        <Form>
          <FormGroup className="mb-4 mt-4">
            <FormLabel>Organization Name*</FormLabel>
            <FormControl
              autoComplete="off"
              onChange={(event) =>
                handleInputChange({ name: event.target.value })
              }
              value={organization?.name}
              type="text"
              placeholder="Organization Name"
              aria-label="Organization Name"
            />
          </FormGroup>

          <Card className="mt-4">
            <Card.Header>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h4>Stripe Subscription*</h4>
                <Button
                  variant="primary"
                  onClick={handleSearchStripeSubscriptionClick}
                >
                  {stripeSubscription?.subscription_id ? 'Change' : '+ Add'}{' '}
                  Stripe Subscription
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {stripeSubscriptionIsFetching ? (
                <div className="m-2">Loading...</div>
              ) : stripeSubscriptionIsError ? (
                <div>
                  Unable to load stripe subscription:{' '}
                  {stripeSubscriptionError.message}
                </div>
              ) : (
                <div
                  style={{
                    overflowX: 'auto',
                    borderRadius: '8px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#6c757d #343a40',
                  }}
                >
                  <style>
                    {`
                      /* Simple, clean scrollbar for table */
                      .custom-scrollbar::-webkit-scrollbar {
                        height: 6px;
                      }
                      .custom-scrollbar::-webkit-scrollbar-track {
                        background: transparent;
                      }
                      .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        border-radius: 3px;
                      }
                      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: rgba(255, 255, 255, 0.3);
                      }
                    `}
                  </style>
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                    variant="dark"
                    className="custom-scrollbar"
                  >
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Seat Count</th>
                        <th>Status</th>
                        <th>Recurring Amount</th>
                        <th>Sale Rep ID</th>
                        <th>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ width: '100px' }}>
                          {stripeSubscription.subscription_id}
                        </td>
                        <td className="text-center" style={{ width: '100px' }}>
                          <FormControl
                            type="number"
                            min="0"
                            value={manualSeatCount}
                            onChange={(e) => setManualSeatCount(e.target.value)}
                            style={{ width: '80px', margin: '0 auto' }}
                          />
                        </td>
                        <td>{stripeSubscription.subscription_status}</td>
                        <td>
                          <FormControl
                            type="number"
                            step="0.01"
                            min="0"
                            value={manualAmount}
                            onChange={(e) => setManualAmount(e.target.value)}
                            style={
                              !stripeSubscription.recurring_amount
                                ? { borderColor: '#ffc107', width: '60%' }
                                : {}
                            }
                            placeholder="Enter amount"
                          />
                        </td>
                        <td style={{ minWidth: 180, position: 'relative' }}>
                          <Select
                            value={
                              salesRepOptions.find(
                                (opt) => opt.value === String(manualSaleRepId)
                              ) || null
                            }
                            onChange={(
                              option: { value: string; label: string } | null
                            ) => setManualSaleRepId(option ? option.value : '')}
                            options={salesRepOptions}
                            placeholder="Select Sales Rep"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base: any) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                              option: (base: any, state: any) => ({
                                ...base,
                                backgroundColor: state.isSelected
                                  ? '#e9ecef'
                                  : state.isFocused
                                  ? '#f1f3f5'
                                  : '#ffffff',
                                color: '#000000',
                                padding: 5,
                                cursor: 'pointer',
                              }),
                              placeholder: (base: any) => ({
                                ...base,
                                color: '#6c757d',
                              }),
                              input: (base: any) => ({
                                ...base,
                                color: '#000000',
                              }),
                            }}
                          />
                        </td>
                        <td>
                          {stripeSubscription.subscription_id && (
                            <Button
                              onClick={() =>
                                window.open(
                                  `https://dashboard.stripe.com/subscriptions/${stripeSubscription.subscription_id}`,
                                  '_blank'
                                )
                              }
                            >
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
          <Card className="mt-4">
            <Card.Header>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h4>
                  Admin User <small>(optional - can be added later)</small>
                </h4>
                <Button
                  variant="primary"
                  onClick={() => setShowUserSearchAndSelectModal(true)}
                >
                  {newAdminUser?.id ? 'Change' : '+ Add'} Org Admin
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <a
                href={`/user/${newAdminUser?.uuid}`}
                target="_blank"
                rel="noreferrer"
              >
                {' '}
                {newAdminUser?.name}{' '}
              </a>
            </Card.Body>
          </Card>
          <Button
            className="mt-4 mb-4"
            size="lg"
            variant="success"
            onClick={(e) => handleSaveClick(e)}
            type="submit"
          >
            Save
          </Button>
        </Form>
        <UserSearchAndSelectModal
          show={showUserSearchAndSelectModal}
          handleClose={() => setShowUserSearchAndSelectModal(false)}
          modalTitle="Add User"
          userButtonText="Add User"
          handleUserButtonClick={handleUserButtonClick}
        />
        <EditStripeSubscriptionIdModal
          show={showEditStripeSubscriptionIdModal}
          handleClose={() => setShowEditStripeSubscriptionIdModal(false)}
          handleSaveClick={handleSaveStripeSubscriptionIdClick}
          initialStripeSubscriptionId={stripeSubscriptionId}
        />
      </div>
    </Container>
  )
}

export default OrganizationCreatePage
